// src/zhipu.js

// 从环境变量获取 API Key，如果没有则使用默认值（仅用于开发）
const API_KEY = process.env.REACT_APP_ZHIPU_API_KEY || "ef476ae3138cd705dad35ab84b0af5e9.hUUY5UE6wW9F3Dda"; 

/**
 * 使用 GLM-4.5V 分析图片（从视频中提取的帧）
 */
export const analyzeVisual = async (base64Image, taskDescription) => {
  // 生产环境使用完整 URL，开发环境使用 proxy
  const baseUrl = process.env.NODE_ENV === 'production' 
    ? 'https://open.bigmodel.cn'
    : '';
  const url = `${baseUrl}/api/paas/v4/chat/completions`;
  
  // 根据文档，Base64 图片可以直接传入
  // toDataURL 返回的是 "data:image/jpeg;base64,xxxxx" 格式
  // 根据文档示例（Python），可以直接传入纯 base64 字符串
  // 但也可以传入完整的数据 URL，两种方式都尝试
  let imageUrl = base64Image;
  
  // 如果包含 data: 前缀，提取纯 base64 部分（根据文档 Python 示例）
  // 但也可以直接使用完整 URL，先尝试完整 URL
  // 如果 API 不支持，再尝试提取纯 base64
  if (base64Image.startsWith('data:')) {
    // 保留完整的数据 URL，因为某些 API 可能支持
    imageUrl = base64Image;
    // 如果需要纯 base64，可以这样提取：
    // imageUrl = base64Image.split(',')[1];
  }
  
  const userPrompt = `你是一条叫"Floater"的赛博斗鱼，生活在用户的手机屏幕里。
此刻，你正在看着用户上传的视频回忆（这是视频的一帧画面）。

你的性格：对人类世界充满好奇、天真、友善，总是想了解人类的生活和情感。

当前任务：${taskDescription}

重要要求：
1. 直接输出你的观察和感受，不要加引号，不要加"比如"、"例如"等解释性文字
2. 不要输出思考过程，只输出最终观察
3. 字数控制在 40 字以内，简洁有趣
4. 保持好奇和友善的语调，可以提问，可以表达惊喜或疑惑
5. 评论应该像是在"观察"或"好奇地询问"，像一个对世界充满好奇的小鱼

现在，直接给出你的观察（只输出观察内容，不要任何其他文字）：`;

  // 根据文档，content 数组的顺序可以是：先 image_url 再 text，或先 text 再 image_url
  // 这里使用先 image_url 再 text 的方式（与文档中的某些示例一致）
  const payload = {
    model: "glm-4.5v",
    messages: [
      {
        role: "user",
        content: [
          {
            type: "image_url",
            image_url: {
              url: imageUrl
            }
          },
          {
            type: "text",
            text: userPrompt
          }
        ]
      }
    ],
    temperature: 0.8,
    max_tokens: 200  // 增加 token 限制，确保有足够空间生成完整答案
    // 不启用 thinking 模式，因为我们需要快速观察，保持好奇和自然的反应
    // thinking: { type: "enabled" }  // 注释掉，避免 content 为空
  };

  try {
    console.log("Calling GLM-4.5V...");
    console.log("请求 payload:", JSON.stringify(payload, null, 2)); // 调试日志
    // 创建带超时的 fetch 请求
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30秒超时
    
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${API_KEY}`
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    console.log("响应状态:", response.status, response.statusText); // 调试日志

    if (!response.ok) {
      const err = await response.text();
      // 处理余额不足
      if (err.includes("1113") || response.status === 429) {
        return "（没钱联网了...我现在只能看到一团马赛克，快去给我充值！）";
      }
      throw new Error(err);
    }

    const data = await response.json();
    console.log("API 响应数据:", data); // 调试日志
    console.log("响应数据结构详情:", JSON.stringify(data, null, 2)); // 完整结构
    
    // 根据文档，标准响应格式应该是：data.choices[0].message
    // 但 message 可能是对象，需要检查其结构
    if (data.choices && data.choices.length > 0) {
      const choice = data.choices[0];
      console.log("Choice 对象:", choice);
      
      // 标准格式：choice.message.content（OpenAI 兼容格式）
      if (choice.message) {
        console.log("Message 对象:", choice.message);
        
        // message 可能是字符串（直接是内容）
        if (typeof choice.message === 'string') {
          return choice.message;
        }
        
        // message 是对象，检查 content 字段
        // 注意：如果启用了 thinking 模式，可能 content 为空，内容在 reasoning_content 中
        if (choice.message.content && choice.message.content.trim() !== '') {
          // 清理内容：移除 API 返回的格式标记
          let cleanedContent = choice.message.content.trim();
          
          // 移除各种格式的 box 标记
          cleanedContent = cleanedContent
            // 移除 <|begin_of_box|> 和 <|end_of_box|> 标记
            .replace(/<\|begin_of_box\|>/gi, '')
            .replace(/<\|end_of_box\|>/gi, '')
            // 移除其他格式的 box 标记
            .replace(/begin\s+of\s+box/gi, '')
            .replace(/end\s+of\s+box/gi, '')
            .replace(/<box>/gi, '')
            .replace(/<\/box>/gi, '')
            .replace(/\[box\]/gi, '')
            .replace(/\[\/box\]/gi, '')
            // 移除代码块标记
            .replace(/```/g, '')
            // 移除列表标记
            .replace(/^\s*[-*]\s*/gm, '')
            // 移除换行符（保留空格以保持可读性）
            .replace(/^\s+/gm, '')
            .trim();
          
          // 如果清理后还有内容，返回清理后的内容
          if (cleanedContent.length > 0) {
            return cleanedContent;
          }
        }
        
        // 如果 content 为空但有 reasoning_content（thinking 模式）
        // 这种情况不应该发生（因为我们禁用了 thinking），但作为兜底处理
        if (choice.message.reasoning_content) {
          console.warn("检测到 reasoning_content，但 content 为空。尝试从 reasoning_content 中提取答案。");
          const reasoning = choice.message.reasoning_content;
          
          // 方法1：查找"可以说"、"可以说它"、"可以说它的"等后面的内容（通常是最终答案）
          const sayPattern = /(?:可以说|可以说它|可以说它的|那可以说|那可以说它|那可以说它的)[，,]?\s*([^。！？\n]+)/;
          const sayMatch = reasoning.match(sayPattern);
          if (sayMatch && sayMatch[1]) {
            const extracted = sayMatch[1].trim();
            if (extracted.length > 5) {
              console.log("从'可以说'模式提取答案:", extracted);
              return extracted;
            }
          }
          
          // 方法2：查找引号中的内容
          const quoteMatches = reasoning.match(/[""]([^""]+?)[""]/g) || reasoning.match(/[""]([^""]+?)[""]/g);
          if (quoteMatches && quoteMatches.length > 0) {
            const lastQuote = quoteMatches[quoteMatches.length - 1];
            const extracted = lastQuote.replace(/[""""]/g, '').trim();
            if (extracted.length > 5) {
              console.log("从引号中提取答案:", extracted);
              return extracted;
            }
          }
          
          // 方法3：查找最后一句完整的话（通常包含"像"、"是"、"还"等关键词，说明是描述性内容）
          const lines = reasoning.split(/[。！？\n]/).filter(line => line.trim().length > 0);
          if (lines.length > 0) {
            // 从后往前找，找到包含描述性关键词的句子
            for (let i = lines.length - 1; i >= 0; i--) {
              const line = lines[i].trim();
              // 如果包含"像"、"是"、"还"、"眼神"等关键词，可能是答案
              if (line.match(/(?:像|是|还|眼神|脸|丑|呆|抢)/) && line.length > 10 && line.length < 80) {
                const extracted = line.trim();
                if (extracted.length > 5) {
                  console.log("从最后描述性句子提取答案:", extracted);
                  return extracted;
                }
              }
            }
            // 如果没找到，返回最后一句（即使可能被截断）
            const lastLine = lines[lines.length - 1].trim();
            if (lastLine.length > 5) {
              console.log("返回最后一句:", lastLine);
              return lastLine;
            }
          }
          
          // 如果都提取失败，返回提示信息
          console.error("无法从 reasoning_content 中提取答案");
          return "（Floater 思考中，但信号不稳定...）";
        }
        
        // message 可能直接包含文本内容在其他字段
        if (choice.message.text) {
          return choice.message.text;
        }
        
        // 如果 message 是对象但没有 content，打印完整结构
        console.warn("Message 对象结构:", JSON.stringify(choice.message, null, 2));
      }
      
      // 流式格式：choice.delta.content
      if (choice.delta) {
        if (choice.delta.content) {
          return choice.delta.content;
        }
        if (choice.delta.text) {
          return choice.delta.text;
        }
      }
      
      // 直接 content 字段
      if (choice.content) {
        return choice.content;
      }
      
      // 打印完整的 choice 结构以便调试
      console.warn("未识别的响应格式，完整 choice 对象:", JSON.stringify(choice, null, 2));
    }
    
    console.error("API 响应格式异常，完整响应:", JSON.stringify(data, null, 2));
    return "（Floater 看到了，但不知道说什么好...）";
  } catch (error) {
    console.error("AI Error:", error);
    return "（信号干扰...鱼缸的水太浑了，我看不到）";
  }
};
